import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { UserRole } from '../types/userRole';
import { stableStringify } from '@Utils/searchParams';

type UserRolesResponse = { data: UserRole[]; meta: Record<string, any> };
type DefaultError = unknown;

type UseUserRolesParams<S extends Record<string, any> = Record<string, any>> = {
  /** Optional query string params to forward to service */
  searchParams?: S;
  /** Override default enabled behavior */
  enabled?: boolean;
};

export function useUserRoles<TData = UserRolesResponse, TError = DefaultError>(
  { searchParams, enabled = true }: UseUserRolesParams = {},
  options?: Omit<
    UseQueryOptions<UserRolesResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  return useQuery<UserRolesResponse, TError, TData>({
    queryKey: ['userRoles', stableParams],
    enabled: enabled,
    queryFn: async () => {
      return service.getUserRoles(searchParams ?? {});
    },
    // caller can pass select, staleTime, gcTime, retry, refetchOnWindowFocus, etc.
    ...(options as any),
  });
}
